import { Bot, LoaderCircle, Send, Sparkles, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AskWrennenContext, AskWrennenMessage } from '../types';
import { askWrennen } from '../services/askWrennen';

interface AskWrennenDrawerProps {
  context: AskWrennenContext;
}

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const AskWrennenDrawer = (props: AskWrennenDrawerProps) => {
  const { context } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([] as AskWrennenMessage[]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const emptyStateCopy = useMemo(() => {
    if (context.workspaceSummary.propertyCount > 1) {
      return [
        'What is the biggest risk across this account?',
        'How concentrated is my debt exposure?',
        'Which property looks weakest right now?'
      ];
    }

    if (context.propertyId) {
      return [
        'Why is my selected-year IRR lower than expected?',
        'How is debt affecting this property?',
        'What is the biggest risk in this deal?'
      ];
    }

    return [
      'How do I add a property in Wrennen?',
      'How does shared debt linking work?',
      'What is terminal liquidity?'
    ];
  }, [context.propertyId]);

  const handleSubmit = async (event?: any) => {
    event?.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    const userMessage: AskWrennenMessage = {
      id: uid(),
      role: 'user',
      content: question,
      createdAt: Date.now()
    };

    const nextHistory = [...messages, userMessage];
    setMessages(nextHistory);
    setInput('');
    setIsLoading(true);
    setError('');

    try {
      const response = await askWrennen(question, context, messages.slice(-8));
      setMessages((prev: AskWrennenMessage[]) => [...prev, {
        id: uid(),
        role: 'assistant',
        content: response.answer,
        createdAt: Date.now()
      }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ask Wrennen could not respond.');
    } finally {
      setIsLoading(false);
    }
  };

  const useSuggestedPrompt = (prompt: string) => {
    setInput(prompt);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-300 shadow-2xl shadow-black/40 backdrop-blur"
      >
        <Sparkles size={14} />
        Ask Wrennen
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button
            className="absolute inset-0 bg-slate-950/65 backdrop-blur-[2px]"
            onClick={() => setIsOpen(false)}
            aria-label="Close Ask Wrennen"
          />
          <aside className="relative h-full w-full max-w-xl border-l border-slate-800 bg-slate-950/95 shadow-2xl shadow-black/60">
            <div className="flex h-full flex-col">
              <div className="border-b border-slate-800 px-6 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.24em] text-emerald-500">Ask Wrennen</p>
                    <h2 className="mt-2 text-2xl font-black tracking-tight text-white">Investment intelligence in context.</h2>
                    <p className="mt-2 text-sm text-slate-400">
                      {context.workspaceSummary.propertyCount > 1
                        ? `Using live workspace data from ${context.workspaceSummary.propertyCount} properties, ${context.workspaceSummary.debtCount} debt records, and ${context.workspaceSummary.portfolioCount} portfolios.`
                        : context.propertyId
                        ? `Using live data from ${context.propertyName || 'the selected asset'} and its current dashboard state.`
                        : 'No property is selected, so Ask Wrennen will answer product and workflow questions.'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="rounded-2xl border border-slate-700 bg-slate-900 p-3 text-slate-400 transition-colors hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
                {messages.length === 0 ? (
                  <div className="space-y-5">
                    <div className="rounded-[2rem] border border-slate-800 bg-slate-900/70 p-5">
                      <p className="text-sm font-semibold text-slate-200">Ask about returns, debt, liquidity, reserve targets, or risk.</p>
                      <p className="mt-2 text-sm text-slate-400">
                        {context.workspaceSummary.propertyCount > 1
                          ? 'Responses are grounded in your active workspace, including portfolio and debt-level context.'
                          : 'Responses are grounded in the current property data when it exists.'}
                      </p>
                    </div>
                    <div className="space-y-3">
                      {emptyStateCopy.map((prompt: string) => (
                        <button
                          key={prompt}
                          onClick={() => useSuggestedPrompt(prompt)}
                          className="w-full rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4 text-left text-sm text-slate-300 transition-colors hover:border-slate-700 hover:bg-slate-900"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((message: AskWrennenMessage) => (
                    <div
                      key={message.id}
                      className={`rounded-[1.75rem] border px-4 py-4 ${message.role === 'assistant' ? 'border-slate-800 bg-slate-900/70 text-slate-100' : 'border-emerald-500/20 bg-emerald-500/10 text-emerald-50'}`}
                    >
                      <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em]">
                        {message.role === 'assistant' ? <Bot size={12} className="text-emerald-400" /> : null}
                        <span className={message.role === 'assistant' ? 'text-slate-400' : 'text-emerald-200'}>{message.role === 'assistant' ? 'Wrennen' : 'You'}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm leading-7">{message.content}</p>
                    </div>
                  ))
                )}

                {isLoading && (
                  <div className="rounded-[1.75rem] border border-slate-800 bg-slate-900/70 px-4 py-4 text-slate-100">
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                      <Bot size={12} className="text-emerald-400" />
                      Wrennen
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <LoaderCircle size={14} className="animate-spin text-emerald-400" />
                      Analyzing your current data...
                    </div>
                  </div>
                )}

                {error ? (
                  <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                    {error}
                  </div>
                ) : null}
              </div>

              <form onSubmit={handleSubmit} className="border-t border-slate-800 px-6 py-5">
                <div className="flex items-end gap-3 rounded-[1.75rem] border border-slate-800 bg-slate-900/70 p-3">
                  <textarea
                    value={input}
                    onChange={(event: any) => setInput(event.target.value)}
                    rows={3}
                    placeholder="Ask about returns, debt, reserves, or the biggest risk in this deal."
                    className="min-h-[76px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-white outline-none placeholder:text-slate-500"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 text-white transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send size={14} />
                  </button>
                </div>
              </form>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default AskWrennenDrawer;
